import {
  GetAnalyticsExportItems,
  GetAnalyticsMetric,
  GetAnalyticsMetricCatalogue,
  GetAnalyticsReviews,
  PostAnalyticsExport,
  PostAnalyitcsUserChangeLog,
  PostAnalyticsServices,
  PostAnalyticsUserWorkTime,
  PostAnalyticsUsersGeneral,
  GetCustomerDataForDateSpan,
  GetRevenue,
  PostUserActivityPerDate,
  PostAnalyticsUsers,
} from "@/server-types";
import type { UseMutationOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { get, post } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";

type MutationOpts<TData, TVariables> = Omit<
  UseMutationOptions<TData, AxiosError, TVariables>,
  "mutationFn" | "onError"
> & {
  displayNotification?: boolean;
  onError?: (
    error: AxiosError,
    variables: TVariables,
    context: unknown,
    processedError: string | null,
    processedErrorCode: string | null,
  ) => void;
};

/**
 * POST /analytics/revenue
 */
export const useRevenue = (body: GetRevenue["body"], enableQuery: boolean) => {
  return useCustomQuery({
    queryKey: ["analyticsRevenue", body],
    queryFn: () => post<GetRevenue>("/analytics/revenue", body),
    enabled: enableQuery,
  });
};

/**
 * POST /analytics/customers
 */
export const useCustomerDataForDateSpan = (
  body: GetCustomerDataForDateSpan["body"],
  enableQuery: boolean,
) => {
  return useCustomQuery({
    queryKey: ["analyticsCustomers", body],
    queryFn: () =>
      post<GetCustomerDataForDateSpan>("/analytics/customers", body),
    enabled: enableQuery,
  });
};

/**
 * POST /analytics/users/activity_per_date
 */
export const useUserActivityPerDate = (
  body: PostUserActivityPerDate["body"],
  enabled: boolean,
) => {
  return useCustomQuery({
    queryKey: ["analyticsUserActivity", body],
    queryFn: () =>
      post<PostUserActivityPerDate>("/analytics/users/activity_per_date", body),
    enabled,
    placeholderData: (prevData) => {
      return prevData;
    },
  });
};

/**
 * POST /analytics/users/general
 */
export const useAnalyticsUserGeneral = (
  body: PostAnalyticsUsersGeneral["body"],
  enabled: boolean,
) => {
  return useCustomQuery({
    queryKey: ["analyticsUserGeneral", body],
    queryFn: () =>
      post<PostAnalyticsUsersGeneral>("/analytics/users/general", body),
    enabled,
    placeholderData: (prevData) => {
      return prevData;
    },
  });
};

/**
 * POST /analytics/users/work_time
 */
export const useAnalyticsUserWorkTime = (
  body: PostAnalyticsUserWorkTime["body"],
  enabled: boolean,
) => {
  return useCustomQuery({
    queryKey: ["analyticsUserWorkTime", body],
    queryFn: () =>
      post<PostAnalyticsUserWorkTime>("/analytics/users/work_time", body),
    enabled,
    placeholderData: (prevData) => {
      return prevData;
    },
  });
};

/**
 * POST /analytics/users/change_log
 */
export const useAnalyticsChangeLog = (
  body: PostAnalyitcsUserChangeLog["body"],
  enabled: boolean,
) => {
  return useCustomQuery({
    queryKey: ["analyticsChangeLog", body],
    queryFn: () =>
      post<PostAnalyitcsUserChangeLog>("/analytics/users/change_log", body),
    enabled,
    placeholderData: (prevData) => {
      return prevData;
    },
  });
};

export const useGetAnalyticsUsers = (body: PostAnalyticsUsers["body"]) => {
  return useCustomQuery({
    queryKey: ["useGetAnalyticsUsers", body],
    queryFn: () => post<PostAnalyticsUsers>("/analytics/user", body),
  });
};

export const useAnalyticsServices = (
  body: PostAnalyticsServices["body"],
  enabled: boolean,
) => {
  return useCustomQuery({
    queryKey: ["analyticsServices", body],
    queryFn: () => post<PostAnalyticsServices>("/analytics/services", body),
    enabled,
    placeholderData: (prevData) => {
      return prevData;
    },
  });
};

/**
 * GET /analytics/v2/metrics
 *
 * The shell's bootstrap call: what the tenant may ask for, and the business
 * timezone every period is then bounded on. Fetched once and reused, because
 * a second opinion about where a day ends is how v1 ended up with four.
 */
export const useGetAnalyticsMetricCatalogue = (
  options: { enabled?: boolean } = {},
) => {
  return useCustomQuery({
    queryKey: ["analyticsV2MetricCatalogue"] as const,
    queryFn: () => get<GetAnalyticsMetricCatalogue>("/analytics/v2/metrics"),
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * GET /analytics/v2/metric/:metricId
 *
 * `from` is inclusive and `to` exclusive, both UTC instants the shell has
 * already resolved on the tenant's day edge. Omit `locationIds` for every
 * location — the server refuses an empty list rather than answering zero.
 */
export const useGetAnalyticsMetric = (
  metricId: string,
  period: {
    from: Date;
    to: Date;
    locationIds?: number[];
    userIds?: number[];
    resourceIds?: number[];
    serviceIds?: number[];
    /** Only metrics that compare two periods read it. */
    comparison?: { from: Date; to: Date };
  } | null,
  options: {
    enabled?: boolean;
    /** Keep showing the last answer while the next one loads, for a figure
     *  that animates from the old value to the new. */
    keepPrevious?: boolean;
  } = {},
) => {
  return useCustomQuery({
    queryKey: [
      "analyticsV2Metric",
      metricId,
      period?.from.toISOString(),
      period?.to.toISOString(),
      period?.locationIds ?? null,
      period?.userIds ?? null,
      period?.resourceIds ?? null,
      period?.serviceIds ?? null,
      period?.comparison?.from.toISOString() ?? null,
      period?.comparison?.to.toISOString() ?? null,
    ] as const,
    queryFn: () =>
      get<GetAnalyticsMetric>(
        `/analytics/v2/metric/${metricId}` as GetAnalyticsMetric["path"],
        {
          from: period!.from,
          to: period!.to,
          ...(period!.locationIds?.length
            ? { locationIds: period!.locationIds }
            : {}),
          ...(period!.userIds?.length ? { userIds: period!.userIds } : {}),
          ...(period!.resourceIds?.length
            ? { resourceIds: period!.resourceIds }
            : {}),
          ...(period!.serviceIds?.length
            ? { serviceIds: period!.serviceIds }
            : {}),
          ...(period!.comparison
            ? {
                comparisonFrom: period!.comparison.from,
                comparisonTo: period!.comparison.to,
              }
            : {}),
        } as GetAnalyticsMetric["query"],
      ),
    enabled:
      (options.enabled ?? true) && metricId.length > 0 && period !== null,
    // A section asks for dozens of figures at once. Fresh for five minutes, a
    // return to the tab refetches only the ones older than that, rather than
    // every figure on every return.
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessRefused,
    placeholderData: options.keepPrevious
      ? (previous) => (period !== null ? previous : undefined)
      : undefined,
  });
};

/**
 * GET /analytics/v2/reviews
 *
 * The period's ratings, newest first, with comment, staff and the customer who
 * left each. Offered only where the catalogue's `mayListReviews` is true.
 */
export const useGetAnalyticsReviews = (
  query: {
    from: Date;
    to: Date;
    locationIds?: number[];
    limit: number;
  } | null,
  options: { enabled?: boolean } = {},
) => {
  return useCustomQuery({
    queryKey: [
      "analyticsV2Reviews",
      query?.from.toISOString(),
      query?.to.toISOString(),
      query?.locationIds ?? null,
      query?.limit,
    ] as const,
    queryFn: () =>
      get<GetAnalyticsReviews>("/analytics/v2/reviews", {
        from: query!.from,
        to: query!.to,
        limit: query!.limit,
        ...(query!.locationIds?.length
          ? { locationIds: query!.locationIds }
          : {}),
      } as GetAnalyticsReviews["query"]),
    enabled: (options.enabled ?? true) && query !== null,
    retry: retryUnlessRefused,
    placeholderData: (previous) => previous,
  });
};

/**
 * GET /analytics/v2/export/items
 *
 * The sheets this reader may export, by section, gated as the catalogue is.
 */
export const useGetAnalyticsExportItems = (
  options: { enabled?: boolean } = {},
) => {
  return useCustomQuery({
    queryKey: ["analyticsV2ExportItems"] as const,
    queryFn: () => get<GetAnalyticsExportItems>("/analytics/v2/export/items"),
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessRefused,
  });
};

/**
 * POST /analytics/v2/export
 *
 * An Excel workbook, base64-encoded, of the sheets asked for over the windows
 * the dashboard already resolved.
 */
export const usePostAnalyticsExport = (
  options?: MutationOpts<
    PostAnalyticsExport["response"],
    PostAnalyticsExport["body"]
  >,
) => {
  return useCustomMutation({
    mutationFn: (body: PostAnalyticsExport["body"]) =>
      post<PostAnalyticsExport>("/analytics/v2/export", body),
    ...options,
  });
};

const MAX_METRIC_RETRIES = 3;

// The server already queued a busy reading for as long as it will wait, so each
// retry is another full wait in the same queue and adds to what it waits on.
const MAX_BUSY_RETRIES = 1;

/** A refusal is the reader's permissions, and a timeout is the period's size:
 *  asking again changes neither. A busy refusal is worth asking again, once. */
function retryUnlessRefused(failureCount: number, error: unknown): boolean {
  if (
    error instanceof AxiosError &&
    (error.response?.status === 401 ||
      error.response?.status === 403 ||
      error.response?.data?.code === "QUERY_TIMEOUT")
  ) {
    return false;
  }

  if (
    error instanceof AxiosError &&
    error.response?.data?.code === "ANALYTICS_BUSY"
  ) {
    return failureCount < MAX_BUSY_RETRIES;
  }

  return failureCount < MAX_METRIC_RETRIES;
}
